import React, { Component, ReactNode, FormEvent, ChangeEvent } from 'react';
import './MeasurementNew.scss';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import Alert from 'react-bootstrap/Alert';
import { Measurement } from '../../models/Measurement';
import { MeasurementTypeEnum, SingleUser } from '../../api/types';
import { Api } from '../../api';
import { ERouterPath } from '../../features/router/ERouterPath';
import { ToolsQueryState } from './Tools';
import { ApplicationState } from '../../app/store';
import { connect } from 'react-redux';
import Spinner from 'react-bootstrap/Spinner';
import Meter from '../../features/meter/Meter';
import { IDossierV2 } from '../../models/DossierV2';
import { MeasurementSplitLayout } from './MeasurementSplitLayout';

interface ToolsProps extends RouteComponentProps {
  dossier?: IDossierV2;
  user?: SingleUser;
}

interface MeasurementNewComponentState {
  type?: MeasurementTypeEnum;
  typeError?: string;
  question?: string;
  questionError?: string;
  start?: number;
  startError?: string;
  end?: number;
  endError?: string;
  submitError?: string;
  submitting?: boolean;
  returnedByMeasurementSubmit?: boolean;
  returnedByMeasurementCreated?: boolean;
}

export enum MeasurementQueryState {
  MEASUREMENT_SUBMITTED = 'state=measurement_submitted',
  MEASUREMENT_CREATED = 'state=measurement_created'
}

class MeasurementNewComponent extends Component<ToolsProps, MeasurementNewComponentState> {
  private static readonly DEFAULT_START = 0;
  private static readonly DEFAULT_END = 10;
  private static readonly INPUT_TYPE = 'type';
  private static readonly INPUT_QUESTION = 'question';
  private static readonly INPUT_START = 'start';
  private static readonly INPUT_END = 'end';

  private static readonly MIN_VAL_START = 0;
  private static readonly MAX_VAL_START = 14;
  private static readonly MIN_VAL_END = 1;
  private static readonly MAX_VAL_END = 15;

  state: MeasurementNewComponentState = {
    start: MeasurementNewComponent.DEFAULT_START,
    end: MeasurementNewComponent.DEFAULT_END,
    type: MeasurementTypeEnum.CREDIBILITY,
    returnedByMeasurementSubmit: this.isReturnedByMeasurementSubmit(this.props.location.search),
    returnedByMeasurementCreated: this.isReturnedByMeasurementCreated(this.props.location.search),
  };

  handleSubmit = (event: FormEvent): void => {
    event.preventDefault();
    this.resetErrors();

    const isValid = this.validate();
    if (!isValid) {
      return;
    }

    const { dossier } = this.props;
    if (!dossier?.id) {
      this.setState({ submitError: 'Er is geen dossier geselecteerd om de meting naar te versturen' });
      return;
    }

    this.setState({ submitError: undefined, submitting: true });
    const { type, question, start, end } = this.state;

    if (type && question && start !== undefined && end !== undefined) {
      Api.createMeasurement({
        type,
        question,
        start: start,
        end: end,
        forFile: dossier.id
      })
        .then(() => this.props.history.push(`${ERouterPath.tools}?${MeasurementQueryState.MEASUREMENT_CREATED}`))
        .catch(err => {
          this.setState({ submitError: 'Versturen van meting mislukt, probeer het later opnieuw' });
          console.error('Failed to create measurement', err);
        })
        .finally(() => this.setState({ submitting: false }));
    } else {
      this.setState({ submitError: 'Versturen van meting mislukt door een onverwachte fout' });
      console.error('Landed in state where the submit is valid, but not all fields are set. This should not happen');
    }
  };

  handleInputChange = (event: ChangeEvent): void => {
    const { name, value } = (event.target as HTMLInputElement);
    switch (name) {
      case MeasurementNewComponent.INPUT_TYPE:
        this.setState({ type: Measurement.resolveType(value), typeError: undefined });
        break;
      case MeasurementNewComponent.INPUT_QUESTION:
        this.setState({ question: value, questionError: undefined });
        break;
      case MeasurementNewComponent.INPUT_START:
        this.setState({ start: +value, startError: undefined });
        break;
      case MeasurementNewComponent.INPUT_END:
        this.setState({ end: +value, endError: undefined });
        break;
    }
  };

  isReturnedByMeasurementSubmit(query: string): boolean {
    return query.includes(ToolsQueryState.MEASUREMENT_SUBMITTED);
  }

  isReturnedByMeasurementCreated(query: string): boolean {
    return query.includes(ToolsQueryState.MEASUREMENT_CREATED);
  }

  private validate(): boolean {
    const { type, question, start, end } = this.state;
    let isValid = true;
    if (!type) {
      this.setState({ typeError: 'Kies een type meting' });
      isValid = false;
    }
    if (!question || question.length === 0) {
      this.setState({ questionError: 'Vul een stelling in' });
      isValid = false;
    }
    if (start === undefined || start < MeasurementNewComponent.MIN_VAL_START) {
      this.setState({ startError: `Kies een laagste waarde van minimaal ${MeasurementNewComponent.MIN_VAL_START}` });
      isValid = false;
    }
    if (end === undefined || end < MeasurementNewComponent.MIN_VAL_END) {
      this.setState({ endError: `Kies een hoogste waarde van minimaal ${MeasurementNewComponent.MIN_VAL_END}` });
      isValid = false;
    } else if (start && start >= end) {
      this.setState({ endError: 'Hoogste waarde moet groter zijn dan de laagste waarde' });
      isValid = false;
    }
    return isValid;
  }

  private resetErrors(): void {
    this.setState({
      typeError: undefined,
      questionError: undefined,
      startError: undefined,
      endError: undefined,
      submitError: undefined,
    });
  }

  render(): ReactNode {
    const { typeError, questionError, startError, endError, submitError, submitting, type, returnedByMeasurementSubmit, returnedByMeasurementCreated } = this.state;

    return (
      <MeasurementSplitLayout>
        { returnedByMeasurementSubmit && <Alert className="tools__success-message" variant="success">Meting verstuurd!</Alert>}
        { returnedByMeasurementCreated && <Alert className="tools__success-message" variant="success">Nieuwe meting aangemaakt!</Alert>}
        <Form className="new-measurement-form" onSubmit={this.handleSubmit}>
          <Form.Group controlId="newMeasurement.type">
            <Form.Label className="form-label--center">Nieuwe meting</Form.Label>
            <div className="new-measurement-form__type-row">
              <Form.Check
                type="radio"
                name={MeasurementNewComponent.INPUT_TYPE}
                id={MeasurementTypeEnum.CREDIBILITY}
                value={MeasurementTypeEnum.CREDIBILITY}
                onChange={this.handleInputChange}
                label="Geloofwaardigheid"
                defaultChecked
              />
              <Form.Check
                type="radio"
                name={MeasurementNewComponent.INPUT_TYPE}
                id={MeasurementTypeEnum.TENSION}
                value={MeasurementTypeEnum.TENSION}
                onChange={this.handleInputChange}
                label="Spanning"
              />
            </div>
            {typeError && <Alert variant="danger">{typeError}</Alert>}
          </Form.Group>
          {
            type &&
            <Form.Label>{Meter.getQuestionInfoLine(type)}</Form.Label>
          }
          <Form.Group controlId="newMeasurement.question">
            <Form.Control
              type="text"
              name={MeasurementNewComponent.INPUT_QUESTION}
              onChange={this.handleInputChange}
            />
            {questionError && <Alert variant="danger">{questionError}</Alert>}
          </Form.Group>
          <Form.Group className="form-group--values">
            <div className="form-group--value">
              <Form.Label>Laagste waarde</Form.Label>
              <Form.Control
                className="new-measurement-form__steps"
                type="number"
                name={MeasurementNewComponent.INPUT_START}
                min={MeasurementNewComponent.MIN_VAL_START}
                max={MeasurementNewComponent.MAX_VAL_START}
                defaultValue={MeasurementNewComponent.DEFAULT_START}
                onChange={this.handleInputChange}
              />
              {startError && <Alert variant="danger">{startError}</Alert>}
            </div>
            <div className="form-group--value">
              <Form.Label>Hoogste waarde</Form.Label>
              <Form.Control
                className="new-measurement-form__steps"
                type="number"
                name={MeasurementNewComponent.INPUT_END}
                min={MeasurementNewComponent.MIN_VAL_END}
                max={MeasurementNewComponent.MAX_VAL_END}
                defaultValue={MeasurementNewComponent.DEFAULT_END}
                onChange={this.handleInputChange}
              />
              {endError && <Alert variant="danger">{endError}</Alert>}
            </div>
          </Form.Group>
          {submitError && <Alert variant="danger">{submitError}</Alert>}
          <Button type="submit" className="new-measurement-form__submit" disabled={submitting}>
            {submitting && <Spinner animation="border" variant="light" size="sm" />}
            Meting versturen
          </Button>
        </Form>
      </MeasurementSplitLayout>
    );
  }
}

export const MeasurementNew = connect((state: ApplicationState) => ({
  dossier: state.session.dossier,
  user: state.session.user,
}))(withRouter(MeasurementNewComponent));
